/**
 * Pure helpers for extracting files from clipboard paste events.
 * Shared across chat surfaces (ChatInputArea, TaskPrompt) so paste-image
 * behaviour stays consistent.
 */

/**
 * Pull image files out of a paste event's clipboard data.
 *
 * Browsers expose pasted images (screenshots, copy-image actions,
 * drag-source images from another app) as `DataTransferItem`s with
 * `kind === 'file'` and an `image/*` MIME type. Each one is renamed to a
 * timestamped filename so multiple pastes don't all collide on the generic
 * `image.png` that browsers default to — this also gives the user a
 * visibly distinct chip per pasted image.
 */
export const collectClipboardImages = (clipboardData: DataTransfer): File[] => {
  const out: File[] = [];
  const items = clipboardData.items;
  if (!items) return out;
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.kind === 'file' && item.type.startsWith('image/')) {
      const file = item.getAsFile();
      if (file) {
        const ext = file.type.split('/')[1] || 'png';
        const stamp = new Date().toISOString().replaceAll(/[:.]/g, '-');
        const name = `pasted-image-${stamp}-${i}.${ext}`;
        out.push(new File([file], name, { type: file.type }));
      }
    }
  }
  return out;
};
